import { ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Flow = {
  tag: string;
  title: string;
  intro: string;
  steps: ReadonlyArray<string>;
  icon: 'out' | 'in';
};

const FLOWS: ReadonlyArray<Flow> = [
  {
    tag: 'Sens A · tu donnes',
    icon: 'out',
    title: 'Dépôt-vente à un partenaire',
    intro: "Tu confies des produits à un sous-agent ou un revendeur qui les écoule pour toi.",
    steps: [
      'Crée le partenaire et enregistre la consignation : produits + quantités confiées.',
      'Ton stock diminue, le solde dû par le partenaire est suivi en continu.',
      'À chaque vente issue du dépôt, Velko le détecte et met à jour ce qui reste.',
      'Le partenaire règle ou rend l\'invendu : tu clôtures avec une facture QR.',
    ],
  },
  {
    tag: 'Sens B · tu sources',
    icon: 'in',
    title: 'Consignation chez un grossiste',
    intro: "Tu prends des produits chez un partenaire fournisseur et tu ne paies que ce qui se vend.",
    steps: [
      'Ajoute un produit « issu du partenaire » avec son coût et ton prix de vente.',
      'Velko calcule ta marge réelle, déduction faite de ce que tu dois au fournisseur.',
      'Un quota limite ce que tu peux détenir avant de régler ou retourner l\'invendu.',
      'La facture de règlement, avec QR code, se retrouve sur web, desktop et mobile.',
    ],
  },
];

export default function PartenairesHowItWorksV2() {
  return (
    <section className="bg-paper py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>Comment ça marche</SectionLabel>
        <h2 className="mt-3 max-w-3xl font-marketing-display text-[clamp(30px,4.4vw,56px)] font-bold leading-[1.02] tracking-[-0.035em] text-charcoal-800">
          Deux sens. <span className="underline-lime">Une seule logique.</span>
        </h2>
        <p className="mt-4 max-w-2xl font-marketing-body text-[17px] leading-[1.55] text-charcoal-600">
          Que tu confies tes produits ou que tu en prennes en dépôt, Velko suit le stock confié, les
          soldes dus et génère la facture — automatiquement.
        </p>

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {FLOWS.map((flow) => (
            <div
              key={flow.tag}
              className="flex flex-col gap-5 border-2 border-charcoal-800 bg-white p-7 shadow-neobrutal-4 md:p-8"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800">
                  {flow.icon === 'out' ? (
                    <ArrowUpRight className="h-[22px] w-[22px]" strokeWidth={2.4} aria-hidden="true" />
                  ) : (
                    <ArrowDownLeft className="h-[22px] w-[22px]" strokeWidth={2.4} aria-hidden="true" />
                  )}
                </span>
                <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-500">
                  {flow.tag}
                </span>
              </div>

              <div>
                <h3 className="font-marketing-display text-[24px] font-bold leading-[1.1] tracking-[-0.02em] text-charcoal-800">
                  {flow.title}
                </h3>
                <p className="mt-2 font-marketing-body text-[15px] leading-[1.5] text-charcoal-600">
                  {flow.intro}
                </p>
              </div>

              <ol className="space-y-3 border-t-2 border-dashed border-charcoal-200 pt-5">
                {flow.steps.map((step, i) => (
                  <li
                    key={step}
                    className="flex items-start gap-3 font-marketing-body text-[14.5px] leading-[1.5] text-charcoal-700"
                  >
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border-2 border-charcoal-800 bg-paper font-marketing-display text-[12px] font-bold text-charcoal-800">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
